/*
  NOTE: This is pretty much a fork of remark-react
  https://github.com/remarkjs/react-remark

  Currently (as of 18/01/2021) react-remark doesn't support
  server rendering markdown. See below github issue:
  https://github.com/remarkjs/react-remark/issues/16

  Main differences are:
  - secure by default by having the sanitize step within the exported hook
  - uses remark's synchronous 'processSync' method instead of
    process (which is async)
  - adds an additional initialContent option to the useRemark hook
    and uses <Remark /> children prop as the initialContent
  - exports a <RemarkJsx /> component that has a modified hast-util-sanitize
    github schema (default) to allow the custom 'jsx' tag in html (which
    allows us to further parse this in to a react component via jsx-parser).
    This allows us to write jsx in markdown, and render the actual react-component.
  - adds 'className' as a valid attribute to all elements (might only need
    this on jsx tags, todo : look in to this further)
*/

import { Code } from "mdast";
import {
  Fragment,
  FunctionComponent,
  ReactElement,
  createElement,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import rehypeReact, { Options as RehypeReactOptions } from "rehype-react";
import remarkParse, { RemarkParseOptions } from "remark-parse";
import remarkToRehype from "remark-rehype";
import unified, { PluggableList } from "unified";

import rehypeSanitize from "rehype-sanitize";

import deepMerge from "deepmerge";
import GHSCHEMA from "hast-util-sanitize/lib/github.json";
import u from "unist-builder";

type PartialBy<T, K extends keyof T> = Omit<T, K> & Partial<Pick<T, K>>;

export interface UseRemarkOptions {
  remarkParseOptions?: Partial<RemarkParseOptions>;
  remarkToRehypeOptions?: RemarkToRehypeOptions;
  rehypeReactOptions?: PartialBy<
    RehypeReactOptions<typeof createElement>,
    "createElement"
  >;
  remarkPlugins?: PluggableList;
  rehypePlugins?: PluggableList;
  onError?: (err: Error) => void;
  initialContent?: string;
}

const SCHEMA = deepMerge(GHSCHEMA, {
  tagNames: ["jsx"],
  attributes: {
    "*": ["className"],
  },
});

type Test<T extends unified.Plugin> = T extends unified.Plugin<infer U>
  ? U
  : never;

type RemarkToRehypeOptions = Test<typeof remarkToRehype>[1];

export const useRemark = ({
  remarkParseOptions,
  remarkToRehypeOptions,
  rehypeReactOptions,
  remarkPlugins = [],
  rehypePlugins = [],
  initialContent,
}: UseRemarkOptions = {}): [ReactElement | null, (source: string) => void] => {
  const parser = useCallback((source: string) => {
    const result = unified()
      .use(remarkParse, remarkParseOptions)
      .use(remarkPlugins)
      .use(remarkToRehype, remarkToRehypeOptions)
      .use(rehypeSanitize, SCHEMA as any)
      .use(rehypePlugins)
      .use(rehypeReact, {
        createElement,
        Fragment,
        ...rehypeReactOptions,
      } as RehypeReactOptions<typeof createElement>)
      .processSync(source).result as ReactElement;

    return result;
  }, []);

  const [reactContent, setReactContent] = useState<ReactElement | null>(() =>
    initialContent ? parser(initialContent) : null
  );

  const setMarkdownSource = useCallback((source: string) => {
    const result = parser(source);
    setReactContent(result);
  }, []);

  return [reactContent, setMarkdownSource];
};

export interface RemarkProps extends UseRemarkOptions {
  children: string;
}

export const Remark: FunctionComponent<RemarkProps> = ({
  children,
  ...useRemarkOptions
}) => {
  const [reactContent, setMarkdownSource] = useRemark({
    ...useRemarkOptions,
    initialContent: children,
  });

  useEffect(() => {
    setMarkdownSource(children);
  }, [children, setMarkdownSource]);

  return reactContent;
};

interface RemarkJsxProps extends RemarkProps {
  renderJsx: (props: rehypeReact.ComponentProps) => JSX.Element;
}

const remarkJsxToRehypeOptions: RemarkToRehypeOptions = {
  allowDangerousHtml: true,
  handlers: {
    code: (h, genericNode) => {
      const node = genericNode as Code;

      const value = node.value ? node.value + "\n" : "";
      const lang = node.lang?.match(/^[^ \t]+(?=[ \t]|$)/);
      const props: Record<string, unknown> = {};

      if (lang) {
        props.language = lang[0];
        props.className = ["language-" + lang[0]];
      }

      if (lang && lang[0] === "jsx") {
        return h(node, "jsx", props, [u("text", value)]);
      }

      return h(node, "pre", [h(node, "code", props, [u("text", value)])]);
    },
  },
};

export const RemarkJsx: FunctionComponent<RemarkJsxProps> = ({
  children,
  renderJsx,
  rehypeReactOptions: initialRehypeReactOptions,
}) => {
  const rehypeReactOptions = useMemo(
    () =>
      deepMerge(
        {
          passNode: true,
          components: {
            jsx: renderJsx,
          },
        },
        initialRehypeReactOptions || {}
      ),
    []
  );

  return (
    <Remark
      remarkToRehypeOptions={remarkJsxToRehypeOptions}
      rehypeReactOptions={rehypeReactOptions}
    >
      {children}
    </Remark>
  );
};
